import { Injectable } from '@angular/core';
import { SelectedSourceDataPipe } from 'src/app/shared/pipes/selected-source-data.pipe';
import { PropertyMap } from '../../order/interface/order.interface';
import { ApproachFactoryState } from '../../order/state/approach-factory.state';
import { SyncState } from '../../sync/state/sync.state';
import { ExtendedPropertyDetail, PropertySummary } from '../interface/property.interface';

/**
 * Owns `property.summary.*` rollup recompute and the baseline reconciliation that keeps those
 * derived values local (shown live but never call /updateOrder because the backend derives them).
 */
@Injectable({
  providedIn: 'root',
})
export class PropertySummaryService {
  /** Structure/unit field edits that require recomputing `property.summary.*` locally. */
  private readonly _summaryTriggerFields = new Set<string>([
    'BedroomsTotal',
    'BathroomsHalf',
    'BathroomsFullCalculated',
    'BathroomsHalfCalculated',
    'YearBuilt',
  ]);

  constructor(
    private approachFactoryState: ApproachFactoryState,
    private selectedSourceDataPipe: SelectedSourceDataPipe,
    private syncState: SyncState
  ) {}

  /** Recomputes and writes `property.summary` in place. */
  public applySummary(property: ExtendedPropertyDetail): void {
    const newSummary = this.recomputeSummary(property);
    if (newSummary) property.summary = newSummary;
  }

  /**
   * Recomputes `summary` in place on a detached property object. Used where there is no sync baseline to
   * patch, so unlike the subject/comparable variants it only touches the object handed to it.
   */
  public reconcilePropertySummary(fieldKey: string, property: ExtendedPropertyDetail): void {
    if (!this._summaryTriggerFields.has(fieldKey)) return;

    this.applySummary(property);
  }

  public reconcileSubjectSummary(fieldKey: string, subject: ExtendedPropertyDetail): void {
    if (!this._summaryTriggerFields.has(fieldKey)) return;

    const newSummary = this.recomputeSummary(subject);
    if (newSummary) this._preSyncSubjectSummary(newSummary, subject);
  }

  public reconcileComparableSummary(fieldKey: string, propertyId: string, map: PropertyMap): void {
    if (!this._summaryTriggerFields.has(fieldKey)) return;
    if (!map[propertyId]) return;

    const newSummary = this.recomputeSummary(map[propertyId]);
    if (newSummary) this._preSyncCompSummary(propertyId, newSummary, map);
  }

  public recomputeSummary(property: ExtendedPropertyDetail): PropertySummary | null {
    const structures = property.structures ?? [];

    // Only structure-bearing properties roll their own values up; elsewhere the backend projection stands.
    if (!structures.length) return null;

    const bedsTotal: number[] = [];
    const bathsHalf: number[] = [];
    const bathsFullCalculated: number[] = [];
    const bathsHalfCalculated: number[] = [];
    const yearsBuilt: number[] = [];

    for (const structure of structures) {
      if (!structure) continue;

      const yearBuilt = Number(this.selectedSourceDataPipe.transform(structure.YearBuilt));
      if (Number.isFinite(yearBuilt)) yearsBuilt.push(yearBuilt);

      for (const unit of structure.units ?? []) {
        if (!unit) continue;

        const beds = Number(this.selectedSourceDataPipe.transform(unit.BedroomsTotal));
        if (Number.isFinite(beds)) bedsTotal.push(beds);

        const halfBath = Number(this.selectedSourceDataPipe.transform(unit.BathroomsHalf));
        if (Number.isFinite(halfBath)) bathsHalf.push(halfBath);

        const fullBathCalc = Number(this.selectedSourceDataPipe.transform(unit.BathroomsFullCalculated));
        if (Number.isFinite(fullBathCalc)) bathsFullCalculated.push(fullBathCalc);

        const halfBathCalc = Number(this.selectedSourceDataPipe.transform(unit.BathroomsHalfCalculated));
        if (Number.isFinite(halfBathCalc)) bathsHalfCalculated.push(halfBathCalc);
      }
    }

    // Every rollup is rewritten on every pass, including back to empty. Assigning only the fields that still
    // have values would leave a stale total behind when units are removed from the property.
    return {
      ...(property.summary ?? {}),
      summary_BedroomsTotal: this._sum(bedsTotal),
      summary_BathroomsHalf: this._sum(bathsHalf),
      summary_BathroomsFullCalculated: this._sum(bathsFullCalculated),
      summary_BathroomsHalfCalculated: this._sum(bathsHalfCalculated),
      summary_YearBuilt: yearsBuilt.length ? Math.min(...yearsBuilt) : undefined,
    };
  }

  /** Totals the collected values, or undefined when nothing contributed one. */
  private _sum(values: number[]): number | undefined {
    if (!values.length) return undefined;

    return values.reduce((total, value) => total + value, 0);
  }

  private _preSyncSubjectSummary(newSummary: PropertySummary, mutatedSubject: ExtendedPropertyDetail): void {
    mutatedSubject.summary = newSummary;

    const syncedSubject = this.syncState.syncedSubjectPropertyData;
    if (!syncedSubject) return;

    // Patch ONLY summary into the last-synced baseline. Basing this on the synced baseline rather
    // than the live subject keeps field edits made earlier in the same debounce window un-synced,
    // so they are still sent on the next diff instead of being absorbed into the baseline.
    this.syncState.syncedSubjectPropertyData = { ...syncedSubject, summary: newSummary };
  }

  private _preSyncCompSummary(propertyId: string, newSummary: PropertySummary, mutatedMap: PropertyMap): void {
    mutatedMap[propertyId] = { ...mutatedMap[propertyId], summary: newSummary };

    // Patch ONLY summary into the last-synced baseline (see _preSyncSubjectSummary). Using the
    // synced baseline instead of the live map prevents earlier un-synced field edits within the
    // debounce window from being folded into the baseline and dropped from the next diff.
    const syncedMap = structuredClone(this.approachFactoryState.syncedSelectedComparableMap);
    if (!syncedMap[propertyId]) return;

    syncedMap[propertyId] = { ...syncedMap[propertyId], summary: newSummary };
    this.approachFactoryState.syncedSelectedComparableMap = syncedMap;
  }
}
